import { useEffect, useRef } from 'react';
import type { MapContainerRef } from '../components/map/MapContainer';
import { fetchPendingSurveyGeometries } from '../services/api';
import { shouldFetchPendingSurveyGeometries } from '../utils/pendingSurveyGeometries';

type Options = {
  enabled: boolean;
  mapRef: React.RefObject<MapContainerRef | null>;
  isMapLoaded: boolean;
  mapCenter: { lng: number; lat: number };
  mapZoom: number;
  campaignId?: string | null;
  refreshNonce?: number;
};

/** Load signed-in user's pending road lines for dashed map overlay + snap context. */
export function usePendingSurveyGeometries({
  enabled,
  mapRef,
  isMapLoaded,
  mapCenter,
  mapZoom,
  campaignId,
  refreshNonce = 0,
}: Options): void {
  const lastFetchKeyRef = useRef<string>('');

  useEffect(() => {
    if (!enabled || !isMapLoaded) {
      mapRef.current?.setPendingSurveyGeometries(null);
      lastFetchKeyRef.current = '';
      return;
    }

    const map = mapRef.current?.getMap();
    if (!map) return;

    let cancelled = false;
    const debounceId = window.setTimeout(() => {
      const bounds = map.getBounds();
      if (!bounds) return;
      const bbox = {
        west: bounds.getWest(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        north: bounds.getNorth(),
      };

      if (!shouldFetchPendingSurveyGeometries(bbox)) {
        lastFetchKeyRef.current = '';
        mapRef.current?.setPendingSurveyGeometries(null);
        return;
      }

      const fetchKey = `${bbox.west.toFixed(4)}:${bbox.south.toFixed(4)}:${bbox.east.toFixed(4)}:${bbox.north.toFixed(4)}:${campaignId ?? ''}:${refreshNonce}`;
      if (fetchKey === lastFetchKeyRef.current) return;
      lastFetchKeyRef.current = fetchKey;

      void fetchPendingSurveyGeometries(bbox, campaignId)
        .then((fc) => {
          if (!cancelled) {
            mapRef.current?.setPendingSurveyGeometries(fc);
          }
        })
        .catch(() => {
          if (!cancelled) {
            mapRef.current?.setPendingSurveyGeometries(null);
          }
        });
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(debounceId);
    };
  }, [
    enabled,
    isMapLoaded,
    mapCenter.lng,
    mapCenter.lat,
    mapZoom,
    campaignId,
    refreshNonce,
    mapRef,
  ]);
}
